/**
 * Utility functions for formatting field names into human-readable labels
 */

/**
 * Formats a HubSpot field name into a human-readable label
 * Removes underscores and capitalizes words
 * @param fieldName - The raw field name (e.g., "first_name", "last_name")
 * @returns Formatted label (e.g., "First Name", "Last Name")
 */
export const formatHubSpotFieldLabel = (fieldName: string): string => {
  if (!fieldName) return '';
  
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formats a Veracross field name into a human-readable label
 * Removes underscores and capitalizes words
 * @param fieldName - The raw field name
 * @returns Formatted label
 */
export const formatVeracrossFieldLabel = (fieldName: string): string => {
  if (!fieldName) return '';
  
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Gets the best available label for a HubSpot field
 * Prioritizes the provided label, then falls back to formatting the name
 * @param property - HubSpot property object
 * @returns The best available label
 */
export const getHubSpotFieldLabel = (property: { name: string; label?: string }): string => {
  if (property.label && property.label.trim()) {
    return property.label;
  }
  return formatHubSpotFieldLabel(property.name);
};

/**
 * Gets the best available label for a Veracross field
 * Prioritizes the provided label, then falls back to formatting the name
 * @param field - Veracross field object
 * @returns The best available label
 */
export const getVeracrossFieldLabel = (field: { veracrossFieldName: string; veracrossFieldLabel?: string }): string => {
  if (field.veracrossFieldLabel && field.veracrossFieldLabel.trim()) {
    return field.veracrossFieldLabel;
  }
  return formatVeracrossFieldLabel(field.veracrossFieldName);
};
